import { useState, useRef, useEffect } from 'react';

interface CarouselItem {
  name: string;
  description: string;
  image: string;
}

interface MenuCarouselProps {
  title: string;
  items: CarouselItem[];
  featureImage: string;
  imageOnLeft?: boolean;
}

export default function MenuCarousel({ title, items, featureImage, imageOnLeft = false }: MenuCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      checkScroll();
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 320;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const FeatureImg = (
    <div className="hidden lg:block w-[340px] lg:w-[380px] flex-shrink-0 rounded-2xl overflow-hidden">
      <img
        src={featureImage}
        alt={`D'CURE ${title}`}
        className="w-full h-full object-cover"
      />
    </div>
  );

  const Cards = (
    <div className="flex-1 min-w-0">
      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {items.map((item) => (
          <div
            key={item.name}
            className="flex-shrink-0 w-[240px] md:w-[260px] bg-background-50 rounded-2xl p-5 flex flex-col gap-3 hover:scale-[1.02] transition-transform duration-300 cursor-pointer"
          >
            <div className="w-full aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            </div>
            <h4 className="font-body text-base font-semibold text-foreground-900 leading-snug">
              {item.name}
            </h4>
            <p className="font-label text-xs text-foreground-500 leading-relaxed">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const NavArrows = (
    <div className="flex items-center gap-2 mt-4 lg:mt-0 lg:absolute lg:top-1/2 lg:-translate-y-1/2 lg:right-0 z-10">
      <button
        onClick={() => scroll('left')}
        disabled={!canScrollLeft}
        className="w-10 h-10 rounded-full border border-primary-400/30 flex items-center justify-center text-primary-400 hover:bg-primary-500 hover:text-background-950 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
        aria-label="Scroll left"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
        className="w-10 h-10 rounded-full border border-primary-400/30 flex items-center justify-center text-primary-400 hover:bg-primary-500 hover:text-background-950 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
        aria-label="Scroll right"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </div>
  );

  return (
    <div className="mb-16 md:mb-20">
      <h3 className="font-cursive italic text-4xl md:text-5xl text-foreground-50 mb-8 text-center lg:text-left">
        {title}
      </h3>
      <div className="relative flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
        {imageOnLeft && FeatureImg}
        {Cards}
        {!imageOnLeft && FeatureImg}
        {NavArrows}
      </div>
    </div>
  );
}